import type { TableBrowserKeyAlignment, TableBrowserLayout, TableBrowserSidebarMode } from '.';
import type { TableBrowserColumnState } from './descriptorColumnTemplate';
import { tableBrowserColumnStateSchema } from './descriptorColumnTemplate';
import { signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { SyncStorageService } from 'src/services/SyncStorageService';
import { z } from 'zod';

const LAYOUT_KEY = 'admin-table-browser:layout';
const COLUMNS_KEY_PREFIX = 'admin-table-browser:columns:';

export const DEFAULT_SIDEBAR_WIDTH = 360;
export const MIN_SIDEBAR_WIDTH = 350;

// Each field falls back on its own, so one stale or invalid value never resets the others.
const layoutSchema = z.object({
    sidebarWidth: z.number().min(MIN_SIDEBAR_WIDTH).catch(DEFAULT_SIDEBAR_WIDTH),
    sidebarMode: z.enum(['flat', 'chain']).catch('flat'),
    keyAlignment: z.enum(['start', 'end']).catch('start'),
    panelSizes: z.array(z.number().positive()).catch([]),
});

type PersistedLayout = z.output<typeof layoutSchema>;

function readJson<TSchema extends z.ZodTypeAny>(
    storage: SyncStorageService,
    key: string,
    schema: TSchema,
): z.output<TSchema> | null {
    const raw = storage.getItem(key);
    if (raw === null) {
        return null;
    }
    try {
        const result = schema.safeParse(JSON.parse(raw));
        return result.success ? result.data : null;
    } catch {
        return null;
    }
}

/** Device-level preferences behind the synchronous storage binding, so tests can bind a fake. */
export class TableBrowserLayoutModel implements TableBrowserLayout {
    readonly #storage: SyncStorageService;
    readonly #log: Logger | undefined;
    readonly #sidebarWidth: Signal<number>;
    readonly #sidebarMode: Signal<TableBrowserSidebarMode>;
    readonly #keyAlignment: Signal<TableBrowserKeyAlignment>;
    readonly #panelSizes: Signal<readonly number[]>;

    constructor(storage: SyncStorageService, log?: Logger) {
        this.#storage = storage;
        this.#log = log;
        const persisted = readJson(storage, LAYOUT_KEY, layoutSchema) ?? layoutSchema.parse({});
        this.#sidebarWidth = signal(persisted.sidebarWidth);
        this.#sidebarMode = signal(persisted.sidebarMode);
        this.#keyAlignment = signal(persisted.keyAlignment);
        this.#panelSizes = signal<readonly number[]>(persisted.panelSizes);
    }

    get sidebarWidth(): ReadonlySignal<number> {
        return this.#sidebarWidth;
    }

    get sidebarMode(): ReadonlySignal<TableBrowserSidebarMode> {
        return this.#sidebarMode;
    }

    get keyAlignment(): ReadonlySignal<TableBrowserKeyAlignment> {
        return this.#keyAlignment;
    }

    get panelSizes(): ReadonlySignal<readonly number[]> {
        return this.#panelSizes;
    }

    setSidebarWidth(width: number): void {
        const clamped = Math.max(MIN_SIDEBAR_WIDTH, Math.round(width));
        if (this.#sidebarWidth.value === clamped) {
            return;
        }
        this.#sidebarWidth.value = clamped;
        this.#persist();
    }

    setSidebarMode(mode: TableBrowserSidebarMode): void {
        if (this.#sidebarMode.value === mode) {
            return;
        }
        this.#sidebarMode.value = mode;
        this.#persist();
    }

    setKeyAlignment(alignment: TableBrowserKeyAlignment): void {
        if (this.#keyAlignment.value === alignment) {
            return;
        }
        this.#keyAlignment.value = alignment;
        this.#persist();
    }

    setPanelSizes(sizes: readonly number[]): void {
        const current = this.#panelSizes.value;
        if (current.length === sizes.length && current.every((size, index) => size === sizes[index])) {
            return;
        }
        this.#panelSizes.value = [...sizes];
        this.#persist();
    }

    readColumnState(sourceKey: string): TableBrowserColumnState | null {
        return readJson(this.#storage, COLUMNS_KEY_PREFIX + sourceKey, tableBrowserColumnStateSchema);
    }

    writeColumnState(sourceKey: string, state: TableBrowserColumnState): void {
        this.#write(COLUMNS_KEY_PREFIX + sourceKey, JSON.stringify(state));
    }

    /** Storage can refuse writes (quota, private mode); a lost preference must not break the page. */
    #write(key: string, value: string): void {
        try {
            this.#storage.setItem(key, value);
        } catch (error) {
            this.#log?.warn({ message: 'Table Browser preference was not saved', key, error: error as Error });
        }
    }

    #persist(): void {
        const layout: PersistedLayout = {
            sidebarWidth: this.#sidebarWidth.value,
            sidebarMode: this.#sidebarMode.value,
            keyAlignment: this.#keyAlignment.value,
            panelSizes: [...this.#panelSizes.value],
        };
        this.#write(LAYOUT_KEY, JSON.stringify(layout));
    }
}
